import React, { useCallback, useEffect, useState, useRef } from 'react'
import PlatformGrid from '@/components/DataGrid/PlatformGrid'
import type {
    GridColumn,
    GridSort,
    GridFilter,
} from '@/components/DataGrid/PlatformGrid.types'
import type { SDUIComponentProps } from '@/types/sduiTypes'
import {
    postGridDeduped,
    getGridDeduped,
    invalidateGridCache,
} from './gridFetchDedupe'
import BaseService from '@/lib/BaseService'
import Alert from '@/components/Alert'
import { SDUI_GRID_CONFIG } from '../config/gridConfig'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
    setSelectedRowForDts,
    setGridEmptyForDts,
    setCheckedRowsForDts,
    clearCheckedRowsForDts,
    selectSelectedRowForDts,
} from '@/store/slices/it-process/application-management/dts/detailsTabStripsSlice'
import type { ObjResponse } from '@/features/it-process/application-management/sdui/services/ObjService'

interface PaginationState {
    page: number
    totalPages: number
    totalCount: number
    pageSize: number
}

const SDUIGrid: React.FC<SDUIComponentProps> = ({
    id,
    endpoint: endpointProp,
    columns: columnsProp,
    enableFilters: enableFiltersProp,
    enableExport: enableExportProp,
    defaultPageSize: defaultPageSizeProp,
    dtsId,
    selectedRow,
    onRowClick,
}) => {
    // -- 1. gridConfig first ---------------------------------------------------
    const gridConfig = SDUI_GRID_CONFIG[id]

    // -- 2. derived values from gridConfig before any useState -----------------
    const endpoint =
        gridConfig?.endpoint ??
        (typeof endpointProp === 'string' ? endpointProp : undefined)
    const enableFilters =
        gridConfig?.enableFilters ?? enableFiltersProp !== false
    const enableExport = gridConfig?.enableExport ?? enableExportProp === true
    const defaultPageSize =
        gridConfig?.defaultPageSize ??
        (typeof defaultPageSizeProp === 'number' ? defaultPageSizeProp : 20)

    // -- 3. state declarations after defaultPageSize is known ------------------
    const [data, setData] = useState<Record<string, unknown>[]>([])
    const [visibleColumns, setVisibleColumns] = useState<string[]>()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [pagination, setPagination] = useState<PaginationState>({
        page: 1,
        totalPages: 1,
        totalCount: 0,
        pageSize: defaultPageSize,
    })
    const pageSizeRef = useRef(defaultPageSize)
    const [serverSearch, setServerSearch] = useState('')
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const handleSearchChange = useCallback((term: string) => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        searchTimerRef.current = setTimeout(() => setServerSearch(term), 300)
    }, [])

    const [serverSort, setServerSort] = useState<GridSort[]>([])
    const [serverFilters, setServerFilters] = useState<GridFilter[]>([])
    const [serverColumnSearch, setServerColumnSearch] = useState<
        Record<string, string>
    >({})
    const filterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const colSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const handleSortChange = useCallback((sorts: GridSort[]) => {
        setServerSort(sorts)
    }, [])

    const handleFilterChange = useCallback((fs: GridFilter[]) => {
        if (filterTimerRef.current) clearTimeout(filterTimerRef.current)
        filterTimerRef.current = setTimeout(() => setServerFilters(fs), 300)
    }, [])

    const handleColumnSearchChange = useCallback(
        (colSearch: Record<string, string>) => {
            if (colSearchTimerRef.current)
                clearTimeout(colSearchTimerRef.current)
            colSearchTimerRef.current = setTimeout(
                () => setServerColumnSearch(colSearch),
                300,
            )
        },
        [],
    )

    useEffect(
        () => () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
            if (filterTimerRef.current) clearTimeout(filterTimerRef.current)
            if (colSearchTimerRef.current)
                clearTimeout(colSearchTimerRef.current)
        },
        [],
    )

    // selectedRow feeds the request body via gridConfig.rowParams. Read it from a
    // ref so fetchData always sees the latest value without listing the object as
    // a dependency — the auto-fetch is triggered by `rowParamsKey` (the bound
    // values) instead, so grids without rowParams never refetch on an unrelated
    // selection change and grids with rowParams refetch only when their bound
    // values actually change.
    const selectedRowRef = useRef(selectedRow)
    selectedRowRef.current = selectedRow

    const dataRef = useRef(data)
    dataRef.current = data

    // Rows keyed by their idField value, accumulated across every page fetch
    // AND every "select all" / export fetch. `handleSelectionChange` below
    // builds the checked-rows payload from this cache rather than from `data`
    // (only the current page) — otherwise selecting all N records via the grid
    // header checkbox (which selects IDs spanning every page) would collapse
    // down to just the handful of rows visible on the current page once
    // filtered against `data`, silently dropping the rest of the selection.
    const allKnownRowsRef = useRef(new Map<unknown, Record<string, unknown>>())

    const rowParamsKey = gridConfig?.rowParams
        ? Object.entries(gridConfig.rowParams)
              .map(([param, src]) => `${param}=${selectedRow?.[src] ?? ''}`)
              .join('&')
        : ''

    // Incremented by mutation DAL actions (delete, etc.) to force a re-fetch.
    const gridRefreshSeq = useAppSelector(
        (state) => state.sdui?.gridRefreshSeq ?? 0,
    )
    // Per-DTS targeted refresh — only this grid refetches (used by license deletes
    // so the parent node list is not inadvertently re-queried).
    const dtsRefreshSeq = useAppSelector((state) =>
        dtsId != null ? state.sdui?.gridRefreshSeqByDts?.[dtsId] ?? 0 : 0,
    )

    // Grids with `idField` remount PlatformGrid when gridRefreshSeq/dtsRefreshSeq
    // bump (see the PlatformGrid `key` below). React mounts that new instance in
    // the SAME commit as this render — before this component's OWN effects (the
    // ones that call fetchData) get a chance to run, since child effects fire
    // before parent effects. Left alone, the fresh instance's first render would
    // still see the OLD (pre-mutation) `data`, and its one-shot "auto-select
    // first row" effect would permanently lock onto a row from the stale list
    // (previously masked because a stale search/filter often matched the same
    // row; now visible since that scope is cleared above). Clearing `data` here
    // — during render, not from an effect — means the new instance's very first
    // render already sees an empty list and correctly waits for the refetch.
    const [lastSeenGridRefreshSeq, setLastSeenGridRefreshSeq] =
        useState(gridRefreshSeq)
    const [lastSeenDtsRefreshSeq, setLastSeenDtsRefreshSeq] =
        useState(dtsRefreshSeq)
    if (
        gridConfig?.idField &&
        (lastSeenGridRefreshSeq !== gridRefreshSeq ||
            lastSeenDtsRefreshSeq !== dtsRefreshSeq)
    ) {
        setLastSeenGridRefreshSeq(gridRefreshSeq)
        setLastSeenDtsRefreshSeq(dtsRefreshSeq)
        setData([])
        allKnownRowsRef.current.clear()
    }

    // Cancels the in-flight request when a new fetch starts, deps change, or the
    // component unmounts — covers StrictMode's dev double-invoke and rapid tab
    // switches so only one request is ever live. When the scoping row arrives
    // after mount the request re-fires with the bound params and this aborts the
    // earlier (param-less) one, so only the latest result lands. The signal is
    // forwarded into gridFetchDedupe, so when this grid was the request's sole
    // consumer the abort also cancels it ON THE WIRE — freeing the HTTP/1.1
    // connection instead of letting a superseded/unmounted fetch run to
    // completion unused.
    const abortRef = useRef<AbortController | null>(null)

    // Row currently selected on THIS grid's tab (its own dtsId). Drives the
    // default-first-row selection below; we never clobber an existing one.
    const dispatch = useAppDispatch()
    const existingSelectedRow = useAppSelector((state) =>
        dtsId != null ? selectSelectedRowForDts(dtsId)(state) : null,
    )

    // When the ancestor scope changes (e.g. switching from Admin A to Admin B),
    // the rows on screen, this tab's selected row, and any checkbox-checked rows
    // all still belong to the PREVIOUS parent until the re-fetch below completes.
    // Clear all three right away so a Delete triggered during that in-flight
    // window can't act on stale data — either the confirmation showing the
    // previous admin's license name (stale selectedRow), or worse, deleting the
    // previous admin's contract entirely (stale checkedRows, which delete
    // handlers prioritise over selectedRow). clearCheckedRowsForDts removes the
    // entry outright (not set to []) so hasCheckboxInteraction goes back to
    // false and delete falls back to the freshly auto-selected row. Skipped for
    // grids with no rowParams (rowParamsKey is always '') and on initial mount,
    // where there is nothing stale to clear.
    const prevRowParamsKeyRef = useRef(rowParamsKey)
    useEffect(() => {
        if (!gridConfig?.rowParams) return
        if (prevRowParamsKeyRef.current === rowParamsKey) return
        prevRowParamsKeyRef.current = rowParamsKey
        setData([])
        allKnownRowsRef.current.clear()
        if (dtsId != null) {
            dispatch(setSelectedRowForDts({ dtsId, row: null }))
            dispatch(clearCheckedRowsForDts({ dtsId }))
        }
    }, [rowParamsKey, dtsId, dispatch, gridConfig])

    const paginationRef = useRef(pagination)
    paginationRef.current = pagination

    // -- fetch -----------------------------------------------------------------
    const fetchData = useCallback(
        async (page = 1, pageSize = pageSizeRef.current) => {
            if (!endpoint) return
            abortRef.current?.abort()
            const controller = new AbortController()
            abortRef.current = controller
            setLoading(true)
            setError(null)
            try {
                const selected = selectedRowRef.current
                let response
                if (gridConfig?.method === 'POST') {
                    const defaultReq = gridConfig.defaultRequest ?? {}
                    const { GridRequest: _, ...restDefaultReq } =
                        defaultReq as any
                    const operatorMap: Record<string, string> = {
                        equals: 'eq',
                        contains: 'contains',
                        startsWith: 'startsWith',
                        endsWith: 'endsWith',
                        gt: 'gt',
                        lt: 'lt',
                        gte: 'gte',
                        lte: 'lte',
                    }
                    const backendFilters = [
                        ...serverFilters
                            .filter(
                                (f) =>
                                    f.value ||
                                    f.operator === 'between' ||
                                    f.operator === 'dateRange',
                            )
                            .flatMap((f) => {
                                if (
                                    f.operator === 'between' ||
                                    f.operator === 'dateRange'
                                ) {
                                    const parts: {
                                        Field: string
                                        Operator: string
                                        Value: string
                                    }[] = []
                                    if (f.value)
                                        parts.push({
                                            Field: f.field,
                                            Operator: 'gte',
                                            Value: f.value,
                                        })
                                    if (f.value2)
                                        parts.push({
                                            Field: f.field,
                                            Operator: 'lte',
                                            Value: f.value2,
                                        })
                                    return parts
                                }
                                return [
                                    {
                                        Field: f.field,
                                        Operator:
                                            operatorMap[f.operator] ??
                                            f.operator,
                                        Value: f.value,
                                    },
                                ]
                            }),
                        ...Object.entries(serverColumnSearch)
                            .filter(([, val]) => val.trim())
                            .map(([field, val]) => ({
                                Field: field,
                                Operator:
                                    typeof dataRef.current[0]?.[field] ===
                                    'number'
                                        ? 'eq'
                                        : 'contains',
                                Value: val,
                            })),
                    ]
                    const body = {
                        ...restDefaultReq,
                        GridRequest: {
                            page,
                            pageSize,
                            sortDir: serverSort[0]?.direction ?? 'asc',
                            ...(serverSort[0]?.field
                                ? { sortBy: serverSort[0].field }
                                : {}),
                            ...(serverSearch ? { search: serverSearch } : {}),
                            ...(backendFilters.length > 0
                                ? { filters: backendFilters }
                                : {}),
                        },
                        ...(gridConfig.dtsIdParam &&
                            dtsId !== undefined && {
                                [gridConfig.dtsIdParam]: dtsId,
                            }),
                        ...Object.fromEntries(
                            Object.entries(gridConfig.rowParams ?? {})
                                .filter(
                                    ([, src]) => selected?.[src] !== undefined,
                                )
                                .map(([param, src]) => [param, selected![src]]),
                        ),
                    }
                    // Deduped so a remount (tab re-activation / dev StrictMode /
                    // a render-triggered refetch) re-issuing this exact request
                    // shares one network call instead of firing a duplicate.
                    // Passing the signal registers this fetch as one refcounted
                    // CONSUMER of the shared request: the wire request is only
                    // aborted when EVERY consumer has aborted, so one grid's
                    // unmount can no longer kill another grid's identical
                    // in-flight request — but a superseded or unmounted sole
                    // consumer now frees its HTTP connection instead of letting
                    // the response run to completion unused.
                    response = await postGridDeduped(endpoint, body, {
                        signal: controller.signal,
                    })
                } else {
                    response = await getGridDeduped(
                        `${endpoint}?page=${page}&pageSize=${pageSize}`,
                        { signal: controller.signal },
                    )
                }

                // A later fetch (deps changed) or an unmount superseded this one.
                // Still required with wire cancellation: when ANOTHER consumer
                // keeps the shared request alive, its success resolves this
                // (aborted) caller's promise too — guard so a superseded result
                // is never applied.
                if (controller.signal.aborted) return
                const raw = response.data

                let rows: Record<string, unknown>[] = []
                if (Array.isArray(raw)) {
                    rows = raw
                    setPagination({
                        page: 1,
                        totalPages: 1,
                        totalCount: raw.length,
                        pageSize,
                    })
                } else if (Array.isArray(raw?.data)) {
                    rows = raw.data
                    // Some endpoints compute totalPages/totalCount against the
                    // unfiltered record set, then apply column-search filters
                    // per-page — so a later page can legitimately come back
                    // empty even though the server still claims it exists.
                    // Rather than strand the user on a blank "page 2", treat
                    // an empty non-first page as past the real end: drop back
                    // to the last page that had data and cap totalPages so
                    // the pager stops offering the phantom page.
                    if (rows.length === 0 && page > 1) {
                        const lastRealPage = page - 1
                        setPagination((prev) => ({
                            ...prev,
                            totalPages: lastRealPage,
                        }))
                        fetchData(lastRealPage, pageSize)
                        return
                    }
                    setPagination({
                        page: raw.page ?? page,
                        totalPages: raw.totalPages ?? 1,
                        totalCount: raw.totalCount ?? rows.length,
                        pageSize: raw.pageSize ?? pageSize,
                    })
                }

                if (gridConfig?.clientFilter) {
                    const filter = gridConfig.clientFilter
                    rows = rows.filter((row) =>
                        Object.entries(filter).every(([k, v]) => row[k] === v),
                    )
                }

                if (gridConfig?.idField) {
                    const idf = gridConfig.idField
                    // A page-1 fetch means the grid's scope changed (ancestor
                    // switch, search/sort/filter change, dts change) — start
                    // the cache fresh so it doesn't serve rows from a
                    // different scope on the next "select all"/export.
                    if (page === 1) allKnownRowsRef.current.clear()
                    rows.forEach((r) => allKnownRowsRef.current.set(r[idf], r))
                }

                setData(rows)
                // Record whether this tab's grid is empty so the breadcrumb can
                // show "[Tab] - No Selected Data"; on empty also drop this tab's

                if (dtsId != null) {
                    dispatch(
                        setGridEmptyForDts({
                            dtsId,
                            isEmpty: rows.length === 0,
                        }),
                    )
                    if (rows.length === 0) {
                        dispatch(setSelectedRowForDts({ dtsId, row: null }))
                    }
                }
                setVisibleColumns(
                    !Array.isArray(raw) && Array.isArray(raw?.visibleColumns)
                        ? (raw.visibleColumns as string[])
                        : undefined,
                )
            } catch {
                if (controller.signal.aborted) return
                setError('Failed to load data. Please try again.')
            } finally {
                if (!controller.signal.aborted) setLoading(false)
            }
            // `rowParamsKey` is the intentional trigger in place of the `selectedRow`
            // object (read via ref above), so the request re-runs on bound-value
            // changes, not on every new object reference.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            endpoint,
            gridConfig,
            dtsId,
            rowParamsKey,
            serverSearch,
            serverSort,
            serverFilters,
            serverColumnSearch,
        ],
    )

    useEffect(() => {
        fetchData(1)
        return () => abortRef.current?.abort()
    }, [fetchData])

    // Grids with `idField` remount PlatformGrid on a refresh bump (see the
    // `key` below), which resets its internal search/sort/filter/column-search
    // UI back to blank. If we don't also clear the matching serverSearch/
    // serverSort/serverFilters/serverColumnSearch here, the page-1 refetch
    // below stays scoped to whatever was typed before the mutation — e.g.
    // deleting a record found via search can leave the request still filtered
    // on that search term, silently returning zero rows ("No records found")
    // even though the search box now looks empty. Returns true if it cleared
    // an active scope, so the caller can skip its own fetchData(1) and let the
    // resulting fetchData identity change drive the refetch with fresh params.
    const resetSearchScopeIfRemounting = useCallback(() => {
        if (!gridConfig?.idField) return false
        const hadActiveScope =
            serverSearch !== '' ||
            serverSort.length > 0 ||
            serverFilters.length > 0 ||
            Object.keys(serverColumnSearch).length > 0
        if (!hadActiveScope) return false
        setServerSearch('')
        setServerSort([])
        setServerFilters([])
        setServerColumnSearch({})
        return true
    }, [gridConfig, serverSearch, serverSort, serverFilters, serverColumnSearch])

    // Explicit refetch when a mutation (e.g. delete) bumps the global refresh
    // seq. Guarded so it never double-fetches on mount or on unrelated renders.
    const lastRefreshSeqRef = useRef(gridRefreshSeq)
    useEffect(() => {
        if (lastRefreshSeqRef.current === gridRefreshSeq) return
        lastRefreshSeqRef.current = gridRefreshSeq
        // A mutation changed server data — clear the short-lived dedupe cache so
        // this refresh fetches fresh rows instead of reusing a cached response.
        invalidateGridCache()
        if (!resetSearchScopeIfRemounting()) fetchData(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridRefreshSeq])

    // Targeted refetch when only THIS grid's DTS counter is bumped (e.g. license
    // delete). Leaves sibling and parent grids untouched.
    const lastDtsRefreshSeqRef = useRef(dtsRefreshSeq)
    useEffect(() => {
        if (lastDtsRefreshSeqRef.current === dtsRefreshSeq) return
        lastDtsRefreshSeqRef.current = dtsRefreshSeq
        // Same reasoning as the gridRefreshSeq effect above: without this, a
        // cleared-scope (or otherwise unchanged) refetch can match a request
        // still sitting in the short-lived dedupe cache and silently replay the
        // pre-mutation response instead of hitting the network.
        invalidateGridCache()
        if (!resetSearchScopeIfRemounting()) fetchData(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dtsRefreshSeq])

    // Default the breadcrumb's "selected data" for this grid's tab to the FIRST
    // row, so it's always visible without a click. `selectAncestorSelectedRow`
    // skips self, so this never re-scopes the grid itself; it only feeds the
    // breadcrumb (and acts as a sensible default scope for child tabs).
    // previous visit — the remounted table highlights its first row, so the
    // breadcrumb must match. Within the same visit a user row click is kept.
    const selectionInitDtsRef = useRef<number | null>(null)
    useEffect(() => {
        if (dtsId == null || data.length === 0) return
        const isFreshVisit = selectionInitDtsRef.current !== dtsId
        if (isFreshVisit) {
            // A fresh visit remounts the grid, which re-runs PlatformGrid's cosmetic
            // first-row auto-select — clear any stale "user explicitly cleared
            // checkboxes" flag left over from a previous visit so it doesn't silently
            // block actions (e.g. delete) against a row the UI now shows as selected.
            dispatch(clearCheckedRowsForDts({ dtsId }))
        }
        // Within a visit, keep an existing selection only if its row is still in
        // the data. After a delete the selected row may be gone — fall back to
        // the first row so the selection (and breadcrumb) never points at a
        // removed record.
        const idf = gridConfig?.idField
        const stillPresent =
            existingSelectedRow != null &&
            (!idf ||
                data.some(
                    (r) =>
                        r[idf] ===
                        (existingSelectedRow as Record<string, unknown>)[idf],
                ))
        if (!isFreshVisit && stillPresent) return
        selectionInitDtsRef.current = dtsId
        // Re-select the SAME row (matched by id) from the fresh data when a
        // prior selection is still present — this preserves the user's
        // selection across a remount/refetch, e.g. a language change that
        // re-fetches the same records re-localised (its selection is kept by
        // clearDtsListForRefetch({ preserveSelection: true })). Picking the
        // fresh row object (not the stored stale one) also re-localises the
        // breadcrumb's data label. Falls back to the first row when there's no
        // prior selection or it's no longer in the data (e.g. after a delete).
        const preservedRow =
            idf && stillPresent
                ? data.find(
                      (r) =>
                          r[idf] ===
                          (existingSelectedRow as Record<string, unknown>)[idf],
                  )
                : undefined
        dispatch(
            setSelectedRowForDts({
                dtsId,
                row: (preservedRow ?? data[0]) as unknown as ObjResponse,
                sourceObjName: id,
            }),
        )
    }, [data, dtsId, existingSelectedRow, dispatch, id, gridConfig])

    const resolvedColumns: GridColumn<Record<string, unknown>>[] =
        gridConfig?.columns ??
        (columnsProp as GridColumn<Record<string, unknown>>[] | undefined) ??
        (visibleColumns?.length
            ? visibleColumns.map((key) => ({
                  key,
                  label: humanizeLabel(key),
                  sortable: true,
                  ...(gridConfig?.defaultHiddenColumns?.includes(key) && {
                      defaultHidden: true,
                  }),
                  ...(gridConfig?.columnWidths?.[key] !== undefined && {
                      width: gridConfig.columnWidths[key],
                  }),
              }))
            : undefined) ??
        deriveColumns(data)

    // Shared helper: fetches every matching row (page=1, pageSize=totalCount) using
    // a direct BaseService call — bypasses the dedupe cache so it never collides
    // with in-flight page fetches. Used by both export and select-all.
    const fetchAllRows = useCallback(async (): Promise<
        Record<string, unknown>[]
    > => {
        if (!endpoint) return []
        const totalCount = Math.max(paginationRef.current.totalCount, 1)
        const allPageSize = Math.min(totalCount, 10000)
        const selected = selectedRowRef.current

        const fetchPage = async (page: number) => {
            if (gridConfig?.method === 'POST') {
                const defaultReq = gridConfig.defaultRequest ?? {}
                const restDefaultReq = Object.fromEntries(
                    Object.entries(defaultReq).filter(
                        ([k]) => k !== 'GridRequest',
                    ),
                )
                const operatorMap: Record<string, string> = {
                    equals: 'eq',
                    contains: 'contains',
                    startsWith: 'startsWith',
                    endsWith: 'endsWith',
                    gt: 'gt',
                    lt: 'lt',
                    gte: 'gte',
                    lte: 'lte',
                }
                const backendFilters = [
                    ...serverFilters
                        .filter(
                            (f) =>
                                f.value ||
                                f.operator === 'between' ||
                                f.operator === 'dateRange',
                        )
                        .flatMap((f) => {
                            if (
                                f.operator === 'between' ||
                                f.operator === 'dateRange'
                            ) {
                                const parts: {
                                    Field: string
                                    Operator: string
                                    Value: string
                                }[] = []
                                if (f.value)
                                    parts.push({
                                        Field: f.field,
                                        Operator: 'gte',
                                        Value: f.value,
                                    })
                                if (f.value2)
                                    parts.push({
                                        Field: f.field,
                                        Operator: 'lte',
                                        Value: f.value2,
                                    })
                                return parts
                            }
                            return [
                                {
                                    Field: f.field,
                                    Operator:
                                        operatorMap[f.operator] ?? f.operator,
                                    Value: f.value,
                                },
                            ]
                        }),
                    ...Object.entries(serverColumnSearch)
                        .filter(([, val]) => val.trim())
                        .map(([field, val]) => ({
                            Field: field,
                            Operator:
                                typeof dataRef.current[0]?.[field] === 'number'
                                    ? 'eq'
                                    : 'contains',
                            Value: val,
                        })),
                ]
                const body = {
                    ...restDefaultReq,
                    GridRequest: {
                        page,
                        pageSize: allPageSize,
                        sortDir: serverSort[0]?.direction ?? 'asc',
                        ...(serverSort[0]?.field
                            ? { sortBy: serverSort[0].field }
                            : {}),
                        ...(serverSearch ? { search: serverSearch } : {}),
                        ...(backendFilters.length > 0
                            ? { filters: backendFilters }
                            : {}),
                    },
                    ...(gridConfig.dtsIdParam &&
                        dtsId !== undefined && {
                            [gridConfig.dtsIdParam]: dtsId,
                        }),
                    ...Object.fromEntries(
                        Object.entries(gridConfig.rowParams ?? {})
                            .filter(([, src]) => selected?.[src] !== undefined)
                            .map(([param, src]) => [param, selected![src]]),
                    ),
                }
                return BaseService.post(endpoint, body)
            }
            return BaseService.get(
                `${endpoint}?page=${page}&pageSize=${allPageSize}`,
            )
        }

        try {
            // Some environments' APIs silently clamp pageSize (e.g. to 100 or
            // 500) regardless of what we request, so a single request can't
            // be trusted to return every row. Keep requesting subsequent
            // pages with the same pageSize until we've collected everything
            // (per the known totalCount) or the server returns an empty
            // page. A page coming back shorter than requested is NOT a
            // reliable "last page" signal on its own — a backend that clamps
            // every page to a fixed size (e.g. always 500) would return a
            // "short" page on page 1 even though far more rows remain.
            const allRows: Record<string, unknown>[] = []
            let page = 1
            // Safety cap so a server that never signals the end can't hang this loop.
            for (let i = 0; i < 200; i++) {
                const response = await fetchPage(page)
                const raw = response.data
                const rows: Record<string, unknown>[] = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.data)
                      ? raw.data
                      : []
                if (rows.length === 0) break
                allRows.push(...rows)
                if (allRows.length >= totalCount) {
                    break
                }
                page += 1
            }

            let rows = allRows
            if (gridConfig?.clientFilter) {
                const filter = gridConfig.clientFilter
                rows = rows.filter((row) =>
                    Object.entries(filter).every(([k, v]) => row[k] === v),
                )
            }
            if (gridConfig?.idField) {
                const idf = gridConfig.idField
                rows.forEach((r) => allKnownRowsRef.current.set(r[idf], r))
            }
            return rows
        } catch {
            return []
        }
    }, [
        endpoint,
        gridConfig,
        dtsId,
        serverFilters,
        serverColumnSearch,
        serverSort,
        serverSearch,
    ])

    const handleExport = useCallback(() => fetchAllRows(), [fetchAllRows])

    const handleSelectAll = useCallback(() => fetchAllRows(), [fetchAllRows])

    if (error) {
        return (
            <div className="p-4">
                <Alert showIcon type="danger">
                    <p>{error}</p>
                </Alert>
            </div>
        )
    }

    const getServerPagination = () => {
        return {
            page: pagination.page,
            totalPages: pagination.totalPages,
            totalCount: pagination.totalCount,
            pageSize: pagination.pageSize,
            onPageChange: (newPage: number) => {
                fetchData(newPage, pageSizeRef.current)
            },
            onPageSizeChange: (newPageSize: number) => {
                pageSizeRef.current = newPageSize
                setPagination((prev) => ({ ...prev, pageSize: newPageSize }))
                fetchData(1, newPageSize)
            },
        }
    }

    // Stable row identity for selection. Only grids that opt in via `idField`
    // (e.g. license lists ? 'licId'/'contractId') get checkbox-selection tracking
    // and the refresh remount; all other grids keep their existing behaviour.
    const selectionIdField = gridConfig?.idField
    const rowIdField = selectionIdField ?? id

    const handleSelectionChange = (selectedIds: Set<unknown>) => {
        if (dtsId == null || !selectionIdField) return
        // Look rows up in the cross-page cache, not `data` (only the current
        // page) — a "select all" click selects IDs spanning every page, and
        // filtering those against just the current page's rows would drop
        // everything not on the visible page.
        const rows = Array.from(selectedIds)
            .map((sid) => allKnownRowsRef.current.get(sid))
            .filter((row): row is Record<string, unknown> => row != null)
        dispatch(
            setCheckedRowsForDts({
                dtsId,
                rows: rows as unknown as ObjResponse[],
            }),
        )
    }

    return (
        <PlatformGrid<Record<string, unknown>>
            // Only opt-in (idField) grids remount on a refresh bump — this resets
            // the checkbox selection after a mutation (e.g. delete), clearing the
            // stale "N rows selected" banner. Other grids keep a stable key.
            // Include dtsRefreshSeq too: license deletes bump the per-DTS counter
            // (targeted refresh) rather than the global one, so without it here
            // the grid never remounts and the stale selection lingers forever.
            // Deliberately NOT keyed on rowParamsKey: PlatformGrid holds column
            // order/visibility/width as internal state, so remounting on every
            // ancestor switch (e.g. clicking a different Admin row) would reset
            // those customizations far more often than intended. The checkbox
            // banner can go briefly stale after a parent switch, but that's
            // cosmetic — Delete reads checkedRowsByDts (cleared above), not this
            // component's internal selection set, so it can't act on stale rows.
            key={
                selectionIdField
                    ? `${id}-${gridRefreshSeq}-${dtsRefreshSeq}`
                    : id
            }
            enableSorting
            enableColumnResize
            gridId={id}
            data={data}
            columns={resolvedColumns}
            title=""
            description=""
            enableFilters={enableFilters}
            enableExport={enableExport}
            defaultPageSize={defaultPageSize}
            idField={rowIdField}
            loading={loading}
            serverPagination={getServerPagination()}
            onSelectionChange={
                selectionIdField ? handleSelectionChange : undefined
            }
            onRowClick={onRowClick}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            onFilterChange={handleFilterChange}
            onColumnSearchChange={handleColumnSearchChange}
            onExport={enableExport ? handleExport : undefined}
            onSelectAll={handleSelectAll}
        />
    )
}

function deriveColumns(
    rows: Record<string, unknown>[],
): GridColumn<Record<string, unknown>>[] {
    if (rows.length === 0) return []
    return Object.keys(rows[0]).map((key) => ({
        key,
        label: humanizeLabel(key),
        sortable: true,
    }))
}

function humanizeLabel(key: string): string {
    return key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase())
}

SDUIGrid.displayName = 'SDUIGrid'

export default SDUIGrid
